import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/** Clear of the viewport edge, and clear of the trigger. */
const MARGIN = 8;
const GAP = 6;

export interface TooltipProps {
  /**
   * The explanation. Nothing renders when it is empty, so a caller can pass a
   * reason that is sometimes absent without branching at the call site.
   */
  text?: string | null;
  /** Whatever the tooltip is about — a button, a menu row, an icon. */
  children: ReactNode;
  /**
   * The wrapper's own classes. It is a `span`, so a caller that needs it to
   * fill a row has to say so (`block`, `w-full`); the default is inline, which
   * is what an icon beside a label wants.
   */
  className?: string;
  /** Id for the bubble, so the trigger can point `aria-describedby` at it. */
  id?: string;
}

/**
 * A tooltip that survives every way a person can reach the thing it explains.
 *
 * This is the positioning and open/close machinery that `InfoTip` was built
 * around, lifted out so it is not the only control in the app that can carry an
 * explanation. The thing that forced it: a disabled menu item. "Cancel" greyed
 * out with no reason is a dead end, and the reason was being delivered by the
 * browser's native `title` — which arrives after a second of hovering, renders
 * in the OS's chrome rather than the app's, is unreachable from a keyboard,
 * never appears on a touchscreen at all, and on a long sentence wraps wherever
 * the OS feels like.
 *
 * How it opens and — the harder half — how it closes:
 *
 *   hover      opens on enter, closes on leave, unless it was pinned
 *   focus      opens on focus, closes on blur; Tab away and it is gone. Works
 *              because React's onFocus/onBlur are focusin/focusout, so a
 *              wrapper hears its children
 *   click/tap  pins it open, which is the only thing that works on a
 *              touchscreen, where there is no hover to speak of
 *   Escape     closes and unpins, leaving focus where it was — the key is a
 *              dismissal, not a navigation
 *   elsewhere  a press outside the trigger closes it
 *
 * The bubble is `pointer-events-none`. There is nothing in it to click, and
 * letting the pointer land on it would mean tracking hover across two elements
 * with a gap between them — a whole class of flicker that does not arise if the
 * panel is not a target.
 *
 * Rendered into a portal on `document.body`, so no ancestor's `overflow`,
 * `transform` or stacking context can clip it. That is not theoretical here:
 * the two callers sit inside a horizontally scrolling table and inside a menu
 * that is itself portalled, and an in-flow bubble is cut off by both. The trade
 * is that it does not move with the page on its own, so it is repositioned on
 * scroll and resize — with `capture`, so a scroll of any ancestor counts and
 * not only a scroll of the window.
 */
export function Tooltip({ text, children, className = "", id }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  // A pinned tip survives the pointer leaving the trigger. Set by a click,
  // which is the only gesture a touchscreen has.
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPinned(false);
  }, []);

  /**
   * Put the bubble where it fits.
   *
   * Above the trigger by preference, below it when the space above is not
   * enough, and clamped to the viewport on both axes — on a phone the bubble is
   * nearly as wide as the screen, so its left edge is decided by the margin far
   * more often than by the trigger it belongs to.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const anchor = trigger.getBoundingClientRect();
    const { width, height } = tip.getBoundingClientRect();
    const { innerWidth: vw, innerHeight: vh } = window;

    let top = anchor.top - height - GAP;
    if (top < MARGIN) top = anchor.bottom + GAP;
    // Still off the bottom on a short viewport: sit it as low as it goes rather
    // than let it run off the screen.
    if (top + height > vh - MARGIN) top = Math.max(MARGIN, vh - MARGIN - height);

    const centred = anchor.left + anchor.width / 2 - width / 2;
    const left = Math.min(
      Math.max(MARGIN, centred),
      Math.max(MARGIN, vw - MARGIN - width),
    );

    setPos({ top, left });
  }, []);

  // Before paint, so the bubble is never seen at the wrong place first.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, text]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e: PointerEvent) => {
      // The trigger has its own handler; closing here as well would fight it.
      if (triggerRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onScroll = () => place();

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, close, place]);

  // No reason to show means no wrapper behaviour at all — the children stand on
  // their own rather than being wrapped in a span that does nothing.
  if (!text) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={className}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => {
          if (!pinned) setOpen(false);
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onClick={() => {
          const next = !pinned;
          setPinned(next);
          setOpen(next);
        }}
      >
        {children}
      </span>

      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="pointer-events-none fixed z-9999 rounded-lg bg-ink px-2.5 py-1.5 text-[11px] leading-snug text-white shadow-lg"
            style={{
              // Off-screen until measured, so the first paint is never a flash
              // in the top-left corner.
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
              // Narrower than the screen on a phone, and never a single long
              // line on a monitor.
              maxWidth: "min(18rem, calc(100vw - 1rem))",
              width: "max-content",
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

export default Tooltip;
